import {
  Alert,
  AlertIcon,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useCustomToast,
  VStack,
} from '@elkaso-app/web-design';
import { ICrmMasterCuisineData, useDeleteCrmMasterCuisineApi } from 'apis/crm-master-cuisines';
import React, { useCallback } from 'react';

interface DeleteMasterCuisineModalProps {
  isOpen: boolean;
  onClose: () => void;
  masterCuisine: ICrmMasterCuisineData;
}

export const DeleteMasterCuisineModal: React.FC<DeleteMasterCuisineModalProps> = ({
  isOpen,
  onClose,
  masterCuisine,
}) => {
  const toast = useCustomToast();

  const { mutate: deleteMasterCuisine, isLoading: isDeleting } = useDeleteCrmMasterCuisineApi({
    onSuccess: () => {
      toast({
        title: 'Deleted Successfully!',
        description: `Master cuisine "${masterCuisine.name}" has been deleted permanently.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onClose();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to delete master cuisine. Please try again.';
      toast({
        title: 'Deletion Failed',
        description: errorMessage,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
  });

  const handleConfirm = useCallback(() => {
    deleteMasterCuisine({
      id: masterCuisine.id,
    });
  }, [deleteMasterCuisine, masterCuisine.id]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='md' closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Delete Master Cuisine</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align='stretch'>
            <Alert status='warning'>
              <AlertIcon />
              This action cannot be undone. Are you sure you want to delete this master cuisine?
            </Alert>

            <VStack spacing={2} align='start'>
              <Text fontWeight='semibold'>Cuisine Details:</Text>
              <Text>
                <strong>Name:</strong> {masterCuisine.name}
              </Text>
              {masterCuisine.region && (
                <Text>
                  <strong>Region:</strong> {masterCuisine.region}
                </Text>
              )}
              {masterCuisine.country && (
                <Text>
                  <strong>Country:</strong> {masterCuisine.country}
                </Text>
              )}
              <Text>
                <strong>Status:</strong> {masterCuisine.isActive ? 'Active' : 'Inactive'}
              </Text>
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant='outline' mr={3} onClick={onClose} isDisabled={isDeleting}>
            Cancel
          </Button>
          <Button colorScheme='red' onClick={handleConfirm} isLoading={isDeleting} loadingText='Deleting...'>
            Delete Master Cuisine
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
